import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

const T = {
  agniCore:  '#EA580C', agniGlow: '#F97316', vidyaGold: '#C8A84B',
  nightInk:  '#0F1A2E', slate:    '#111827', muted:     '#6B7280',
  border:    '#E5E7EB', surface:  '#F9FAFB', blue:      '#2563EB',
  green:     '#16A34A', violet:   '#9333EA', red:       '#DC2626', teal: '#0891B2',
}

const DAYS       = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"]
const SHORT_DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"]

const ACTIVITY_TYPES = [
  { id:"flashcard",     label:"Flashcard",     icon:"🃏", color:"#1D4ED8", bg:"#EFF6FF", border:"#93C5FD" },
  { id:"worksheet",     label:"Worksheet",     icon:"📝", color:"#7C3AED", bg:"#F5F3FF", border:"#C4B5FD" },
  { id:"activity-card", label:"Activity Card", icon:"🖼️", color:"#059669", bg:"#ECFDF5", border:"#6EE7B7" },
  { id:"storybook",     label:"Story Book",    icon:"📖", color:"#B45309", bg:"#FFFBEB", border:"#FCD34D" },
  { id:"free-play",     label:"Free Play",     icon:"🎮", color:"#EA580C", bg:"#FFF7ED", border:"#FDBA74" },
  { id:"art-craft",     label:"Art & Craft",   icon:"🎨", color:"#BE185D", bg:"#FDF2F8", border:"#F9A8D4" },
  { id:"music",         label:"Music & Rhyme", icon:"🎵", color:"#1D4ED8", bg:"#EFF6FF", border:"#93C5FD" },
  { id:"outdoor",       label:"Outdoor",       icon:"🌿", color:"#15803D", bg:"#F0FDF4", border:"#86EFAC" },
  { id:"assessment",    label:"Assessment",    icon:"📊", color:"#B45309", bg:"#FFFBEB", border:"#FCD34D" },
  { id:"custom",        label:"Custom",        icon:"✨", color:"#6B7280", bg:"#F9FAFB", border:"#D1D5DB" },
]

const DURATIONS = ["10 min","15 min","20 min","30 min","45 min","1 hour"]

const DOMAINS = [
  "Language & Literacy","Cognitive Development","Socio-Emotional","Physical & Motor","Early Numeracy",
]

const DOMAIN_COLORS = {
  "Language & Literacy":"#1D4ED8","Cognitive Development":"#7C3AED","Socio-Emotional":"#BE185D",
  "Physical & Motor":"#059669","Early Numeracy":"#B45309",
}

const THEME_SUGGESTIONS = [
  "🐘 Animals","🍎 Fruits & Food","🚌 Transport","🌸 Seasons",
  "👨‍👩‍👧 Family","🔢 Numbers","🌿 Nature","🏠 Home & Community",
]

const TYPE_TO_MODULE = {
  "flashcard":"flashcard","worksheet":"worksheet","activity-card":"activitycard",
  "storybook":"storybook","free-play":"custom","art-craft":"custom",
  "music":"custom","outdoor":"custom","assessment":"custom","custom":"custom",
}

const TYPE_TO_CONTENT_MODEL = {
  "flashcard":"Flashcard","worksheet":"Worksheet",
  "activity-card":"ActivityCard","storybook":"StoryBook",
}

const TYPE_TO_LEARN_ROUTE = {
  "flashcard":"/learn","worksheet":"/worksheet-learn",
  "activity-card":"/activity-learn","storybook":"/storybook-learn",
}

const BACKEND_DOMAIN_MAP = {
  "language-literacy":"Language & Literacy","cognitive":"Cognitive Development",
  "socio-emotional":"Socio-Emotional","physical-motor":"Physical & Motor","numeracy":"Early Numeracy",
}

const FRONTEND_DOMAIN_MAP = {
  "Language & Literacy":"language-literacy","Cognitive Development":"cognitive",
  "Socio-Emotional":"socio-emotional","Physical & Motor":"physical-motor","Early Numeracy":"numeracy",
}

const MODULE_SOURCES = {
  "flashcard":     { endpoint:"/flashcard",    listKey:"flashcards",    label:"Flashcard Sets"  },
  "worksheet":     { endpoint:"/worksheet",    listKey:"worksheets",    label:"Worksheets"      },
  "activity-card": { endpoint:"/activitycard", listKey:"activityCards", label:"Activity Cards"  },
  "storybook":     { endpoint:"/storybook",    listKey:"storyBooks",    label:"Story Books"     },
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function getMonday(d) {
  // Parse ISO date strings as local time to avoid UTC offset shifting the day
  const date = (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d))
    ? new Date(d + 'T00:00:00') : new Date(d)
  const day = date.getDay()
  // Sunday (0) snaps FORWARD to next Monday (+1), not backward (-6)
  // All other days snap to the Monday of the current week
  const diff = day === 0 ? 1 : (1 - day)
  date.setDate(date.getDate() + diff)
  return date
}

function formatWeek(dateStr) {
  const d = new Date(dateStr), end = new Date(d)
  end.setDate(end.getDate() + 5)
  return `${d.toLocaleDateString("en-IN",{day:"numeric",month:"short"})} – ${end.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}`
}

function getAType(id) {
  return ACTIVITY_TYPES.find(t => t.id === id) || ACTIVITY_TYPES[ACTIVITY_TYPES.length-1]
}

function getTodayIdx() {
  const d = new Date().getDay()
  if (d === 0) return 4
  return Math.min(d-1, 5)
}

function makeEmptyPlan() {
  const weekStart = getMonday(new Date())
  const weekOfStr  = weekStart.toISOString().split('T')[0]
  return {
    title:"Weekly Work Plan", weekOf:weekOfStr,
    scope:"all", childId:null, childLabel:"All Children",
    classroomId:null, classroomLabel:"", theme:"", notes:"",
    days: DAYS.map(day => ({ day, activities: [] })),
  }
}

function planToBackend(plan) {
  return {
    scope: plan.scope,
    ...(plan.scope==="child"     && plan.childId     ? { childId:     plan.childId     } : {}),
    ...(plan.scope==="classroom" && plan.classroomId ? { classroomId: plan.classroomId } : {}),
    weekStart: getMonday(plan.weekOf).toISOString().split('T')[0], title: plan.title, overallGoal: plan.notes||"", weekTheme: plan.theme||"",
    parentCanView: true,
    days: plan.days.map((d,di) => ({
      dayIndex:di, label:d.day, isActive:true, dayNote:"",
      entries: d.activities.map((a,order) => ({
        domain:       FRONTEND_DOMAIN_MAP[a.domain] || "general",
        moduleType:   TYPE_TO_MODULE[a.type]        || "custom",
        contentRef:   a.linkedId   || null,
        contentModel: TYPE_TO_CONTENT_MODEL[a.type] || null,
        contentTitle: a.title, goal: a.notes||a.title,
        durationMin:  parseInt(a.duration)||15,
        materials:"", status: a.completed?"done":"pending", order,
      })),
    })),
  }
}

function backendToPlan(wp) {
  return {
    _backendId:    wp._id,
    title:         wp.title || "Weekly Work Plan",
    weekOf:        wp.weekStart ? new Date(wp.weekStart).toISOString().split("T")[0] : "",
    scope:         wp.classroomId ? "classroom" : wp.child ? "child" : "all",
    childId:       wp.child?._id || wp.child || null,
    childLabel:    wp.child?.name || "All Children",
    classroomId:   wp.classroomId || null,
    classroomLabel:wp.classroom?.name || "",
    theme: wp.weekTheme||"", notes: wp.overallGoal||"",
    days: (wp.days||[]).map(d => ({
      day: d.label,
      activities: (d.entries||[]).map(e => ({
        id:           String(e._id),
        type:         Object.entries(TYPE_TO_MODULE).find(([,v])=>v===e.moduleType)?.[0] || "custom",
        title:        e.contentTitle || e.goal || "Untitled",
        duration:     `${e.durationMin||15} min`,
        notes:        e.goal || "",
        domain:       BACKEND_DOMAIN_MAP[e.domain] || "Language & Literacy",
        completed:    e.status==="done",
        linkedId:     e.contentRef ? String(e.contentRef) : null,
        _entryId:     e._id,
        _status:      e.status,
        _sessionNote: e.sessionNote||"",
      })),
    })),
  }
}

// ─── PDF generators ────────────────────────────────────────────────────────
function generateWeekPDF(plan) {
  const totalActivities = plan.days.reduce((s,d)=>s+d.activities.length,0)
  const dayColumns = plan.days.map(dayPlan => {
    const acts = dayPlan.activities.map(act => {
      const at = getAType(act.type)
      return `<div style="margin-bottom:5px;border-left:3px solid ${at.border};background:${at.bg};border-radius:0 6px 6px 0;padding:5px 7px;">
        <div style="display:flex;align-items:center;gap:3px;margin-bottom:2px;">
          <span style="font-size:10px;">${at.icon}</span>
          <span style="font-size:8px;font-weight:800;color:${at.color};text-transform:uppercase;">${at.label}</span>
          <span style="margin-left:auto;font-size:7.5px;color:#888;background:white;border-radius:8px;padding:1px 4px;">${act.duration}</span>
        </div>
        <div style="font-size:9.5px;font-weight:700;color:#1a1a2e;line-height:1.3;">${act.title}</div>
        ${act.notes?`<div style="font-size:7.5px;color:#666;margin-top:1px;font-style:italic;">${act.notes}</div>`:""}
        ${act.domain?`<div style="font-size:7px;color:${DOMAIN_COLORS[act.domain]||"#888"};margin-top:2px;font-weight:600;">📌 ${act.domain}</div>`:""}
      </div>`}).join("")
    return `<div style="flex:1;min-width:0;padding:0 7px;border-right:1px solid #e0e7f0;">
      <div style="font-weight:800;font-size:9.5px;color:#0F1A2E;text-transform:uppercase;letter-spacing:.5px;padding:5px 0 7px;border-bottom:2px solid #EA580C;margin-bottom:7px;text-align:center;">${dayPlan.day}</div>
      ${acts||`<div style="font-size:8.5px;color:#ccc;text-align:center;padding:10px 0;font-style:italic;">—</div>`}
    </div>`
  }).join("")
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;background:white;}
@media print{@page{size:A4 landscape;margin:0;}div{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
</head><body>
<div style="width:1122px;min-height:793px;padding:18px 22px;background:white;display:flex;flex-direction:column;">
  <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #EA580C;padding-bottom:9px;margin-bottom:12px;">
    <div style="display:flex;align-items:center;gap:9px;">
      <div style="background:#0F1A2E;color:white;font-weight:900;font-size:13px;padding:3px 9px;border-radius:4px;">NISH</div>
      <div style="font-size:11px;font-weight:700;color:#EA580C;">Vidyagni</div>
    </div>
    <div style="text-align:center;">
      <div style="font-size:15px;font-weight:900;color:#0F1A2E;">${plan.title}</div>
      <div style="font-size:8.5px;color:#666;margin-top:2px;">Week of ${formatWeek(plan.weekOf)} · ${plan.childLabel}</div>
    </div>
    <div style="font-size:7.5px;color:#888;">Total: ${totalActivities} activities</div>
  </div>
  <div style="flex:1;display:flex;gap:0;">${dayColumns}</div>
  <div style="border-top:1px solid #e0e7f0;margin-top:8px;padding-top:5px;display:flex;justify-content:space-between;font-size:6.5px;color:#aaa;">
    <span>© NISH Trivandrum · NCAHT · ICMR · Vidyagni</span>
    <span>Printed: ${new Date().toLocaleDateString("en-IN")}</span>
  </div>
</div></body></html>`
  const win = window.open("","_blank"); if(!win) return
  win.document.write(html); win.document.close(); setTimeout(()=>win.print(),700)
}

function generateDayPDF(plan,dayIdx) {
  const dayPlan = plan.days[dayIdx]; if(!dayPlan) return
  const totalMin = dayPlan.activities.reduce((s,a)=>s+(parseInt(a.duration)||0),0)
  const acts = dayPlan.activities.map(act=>{
    const at=getAType(act.type)
    return `<div style="margin-bottom:10px;border-left:4px solid ${at.border};background:${act.completed?"#F0FFF4":at.bg};border-radius:0 10px 10px 0;padding:10px 14px;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
        <span style="font-size:20px;">${at.icon}</span>
        <div style="flex:1;"><div style="font-size:10px;font-weight:800;color:${at.color};text-transform:uppercase;">${at.label}</div>
        <div style="font-size:14px;font-weight:900;color:#1a1a2e;">${act.title}</div></div>
        <div style="background:white;border:1.5px solid ${at.border};border-radius:20px;padding:3px 10px;font-size:11px;font-weight:700;color:${at.color};">⏱ ${act.duration}</div>
        ${act.completed?`<div style="background:#C6F6D5;color:#276749;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;">✅ Done</div>`:""}
      </div>
      ${act.notes?`<div style="font-size:11px;color:#555;margin-top:4px;font-style:italic;">📌 ${act.notes}</div>`:""}
    </div>`}).join("")
  const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;background:white;}
@media print{@page{size:A4 portrait;margin:0;}*{-webkit-print-color-adjust:exact!important;}}</style>
</head><body><div style="padding:24px 28px;">
  <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #EA580C;padding-bottom:9px;margin-bottom:16px;">
    <div style="background:#0F1A2E;color:white;font-weight:900;font-size:12px;padding:3px 8px;border-radius:4px;">NISH</div>
    <div style="text-align:center;"><div style="font-size:18px;font-weight:900;color:#0F1A2E;">${dayPlan.day}</div>
    <div style="font-size:8px;color:#666;">${formatWeek(plan.weekOf)} · ${plan.childLabel}</div></div>
    <div style="font-size:7.5px;color:#888;">${dayPlan.activities.length} activities · ${totalMin} min</div>
  </div>
  ${acts}
</div></body></html>`
  const win=window.open("","_blank"); if(!win) return
  win.document.write(html); win.document.close(); setTimeout(()=>win.print(),700)
}

// ─── Small shared components ───────────────────────────────────────────────
function Chip({ label, color, bg, onRemove }) {
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:bg, color, border:`1.5px solid ${color}33`, borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:700 }}>
      {label}
      {onRemove && <button onClick={onRemove} style={{ background:"none", border:"none", cursor:"pointer", color, padding:0, fontSize:12, lineHeight:1 }}>×</button>}
    </div>
  )
}
function SectionHead({ children }) {
  return <div style={{ fontSize:11, fontWeight:800, color:T.muted, letterSpacing:1.4, textTransform:"uppercase", marginBottom:8 }}>{children}</div>
}
function Card({ children, style }) {
  return <div style={{ background:"#fff", borderRadius:14, border:`1.5px solid ${T.border}`, padding:"16px 18px", ...style }}>{children}</div>
}

// ─── ActivePlanBanner ──────────────────────────────────────────────────────
// Big "this plan is live" banner shown when savedId exists + current week
function ActivePlanBanner({ plan, savedId, isCurrentWeek, completedActs, totalActs, progressPct, onNavigateDay }) {
  if (!savedId || !isCurrentWeek) return null
  const allDone = totalActs > 0 && completedActs === totalActs
  const todayIdx = getTodayIdx()

  return (
    <div style={{
      margin:"12px 28px 0",
      borderRadius:16,
      background: allDone
        ? "linear-gradient(130deg,#14532D 0%,#15803D 100%)"
        : "linear-gradient(130deg,#0F1A2E 0%,#1D3461 100%)",
      boxShadow: "0 8px 32px rgba(0,0,0,.25)",
      padding:"0",
      overflow:"hidden",
    }}>
      {/* Top strip */}
      <div style={{ background:"rgba(255,255,255,.06)", borderBottom:"1px solid rgba(255,255,255,.08)", padding:"8px 22px", display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ width:8, height:8, borderRadius:"50%", background:"#4ADE80", display:"inline-block", boxShadow:"0 0 0 3px rgba(74,222,128,.25)", animation:"wp-pulse 2s infinite" }}/>
        <span style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,.7)", letterSpacing:.8, textTransform:"uppercase" }}>
          Active Plan · {formatWeek(plan.weekOf)}
        </span>
        {plan.childLabel && plan.childLabel!=="All Children" && (
          <span style={{ fontSize:11, color:"rgba(255,255,255,.45)", marginLeft:4 }}>· {plan.childLabel}</span>
        )}
        {allDone && <span style={{ marginLeft:"auto", fontSize:11, fontWeight:700, color:"#4ADE80" }}>🎉 Week complete!</span>}
      </div>

      {/* Main body */}
      <div style={{ padding:"18px 22px", display:"flex", alignItems:"center", gap:24 }}>
        {/* Left: title + progress */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:20, fontWeight:900, color:"white", marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {plan.title}
          </div>
          {/* Progress bar */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
            <div style={{ flex:1, height:10, background:"rgba(255,255,255,.15)", borderRadius:8, overflow:"hidden" }}>
              <div style={{ width:`${progressPct}%`, height:"100%", background: allDone ? "#4ADE80" : "#F97316", borderRadius:8, transition:"width .6s ease" }}/>
            </div>
            <span style={{ fontSize:22, fontWeight:900, color:"white", fontFamily:"'Nunito',sans-serif", minWidth:40 }}>{progressPct}%</span>
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.55)" }}>
            {completedActs} of {totalActs} activities completed this week
          </div>
        </div>

        {/* Per-day dots */}
        <div style={{ display:"flex", gap:6, flexShrink:0 }}>
          {plan.days.map((d,di) => {
            const tot  = d.activities.length
            const done = d.activities.filter(a=>a.completed).length
            const isToday = di===todayIdx
            const allD = tot>0 && done===tot
            const hasAny = tot>0
            return (
              <div key={di} onClick={()=>onNavigateDay&&onNavigateDay(di)}
                style={{ cursor:"pointer", textAlign:"center" }}>
                <div style={{
                  width:40, height:40, borderRadius:10,
                  background: allD ? "#4ADE80" : hasAny ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.05)",
                  border: isToday ? "2.5px solid white" : allD ? "2px solid #4ADE80" : "2px solid rgba(255,255,255,.15)",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                  boxShadow: isToday ? "0 0 0 4px rgba(255,255,255,.15)" : "none",
                  transition:"all .2s",
                }}>
                  <span style={{ fontSize:13, fontWeight:900, color: allD ? "#14532D" : hasAny ? "white" : "rgba(255,255,255,.25)", lineHeight:1 }}>
                    {allD ? "✓" : tot===0 ? "–" : `${done}/${tot}`}
                  </span>
                </div>
                <div style={{ fontSize:9, fontWeight:700, color: isToday?"white":"rgba(255,255,255,.4)", marginTop:4 }}>
                  {SHORT_DAYS[di]}
                  {isToday && <div style={{ width:4, height:4, borderRadius:"50%", background:"white", margin:"2px auto 0" }}/>}
                </div>
              </div>
            )
          })}
        </div>

        {/* Right: stat pills */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, flexShrink:0, minWidth:130 }}>
          <div style={{ background:"rgba(255,255,255,.1)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:24, fontWeight:900, color:"white", fontFamily:"'Nunito',sans-serif", lineHeight:1 }}>{totalActs}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.5)", fontWeight:700, marginTop:2 }}>Total Activities</div>
          </div>
          <div style={{ background: allDone?"rgba(74,222,128,.2)":"rgba(249,115,22,.15)", borderRadius:10, padding:"10px 14px", textAlign:"center", border:`1px solid ${allDone?"rgba(74,222,128,.3)":"rgba(249,115,22,.3)"}` }}>
            <div style={{ fontSize:24, fontWeight:900, color: allDone?"#4ADE80":"#FB923C", fontFamily:"'Nunito',sans-serif", lineHeight:1 }}>{completedActs}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.5)", fontWeight:700, marginTop:2 }}>Completed</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── LoadedExistingBanner ──────────────────────────────────────────────────
// Shown briefly after 409 auto-load
function LoadedExistingBanner({ show }) {
  if (!show) return null
  return (
    <div style={{
      margin:"12px 28px 0",
      borderRadius:12,
      background:"linear-gradient(130deg,#166534 0%,#15803D 100%)",
      padding:"14px 20px",
      display:"flex", alignItems:"center", gap:14, color:"white",
    }}>
      <span style={{ fontSize:28 }}>📂</span>
      <div>
        <div style={{ fontWeight:800, fontSize:15, marginBottom:2 }}>Existing plan loaded automatically</div>
        <div style={{ fontSize:12, opacity:.8 }}>A plan already existed for this week/scope. It's now loaded and ready to edit — all your changes will update this plan.</div>
      </div>
    </div>
  )
}

// ─── ThemeInput ────────────────────────────────────────────────────────────
function ThemeInput({ value, onChange }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position:"relative" }}>
      <div style={{ display:"flex", alignItems:"center", border:`1.5px solid ${T.border}`, borderRadius:8, overflow:"hidden", background:"#fff" }}>
        <input value={value} onChange={e=>onChange(e.target.value)} onFocus={()=>setOpen(true)} onBlur={()=>setTimeout(()=>setOpen(false),160)}
          placeholder="Set theme…" style={{ border:"none", outline:"none", padding:"6px 10px", fontSize:13, fontWeight:600, color:T.agniCore, width:150, fontFamily:"inherit" }}/>
        <button onClick={()=>setOpen(o=>!o)} style={{ background:"#FFF7ED", border:"none", borderLeft:`1.5px solid ${T.border}`, padding:"6px 10px", cursor:"pointer", fontSize:12, color:T.agniCore }}>▾</button>
      </div>
      {open && (
        <div style={{ position:"absolute", top:"100%", left:0, zIndex:100, background:"#fff", border:`1.5px solid ${T.border}`, borderRadius:12, boxShadow:"0 8px 28px rgba(0,0,0,.12)", padding:6, marginTop:4, minWidth:190 }}>
          {THEME_SUGGESTIONS.map(s=>(
            <button key={s} onMouseDown={()=>{onChange(s.replace(/^[^ ]+ /,"")); setOpen(false)}}
              style={{ display:"block", width:"100%", textAlign:"left", background:"none", border:"none", padding:"7px 12px", fontSize:13, color:T.slate, cursor:"pointer", borderRadius:8 }}
              onMouseEnter={e=>e.target.style.background="#FFF7ED"}
              onMouseLeave={e=>e.target.style.background="none"}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── ModuleItemPicker ──────────────────────────────────────────────────────
function ModuleItemPicker({ type, getAuthHeader, onSelect, onManual }) {
  const [items,setItems]=useState([]), [loading,setLoading]=useState(true), [search,setSearch]=useState("")
  const src=MODULE_SOURCES[type]
  useEffect(()=>{
    if(!src){setLoading(false);return}
    setLoading(true)
    ;(async()=>{
      try{
        const q=search?`?search=${encodeURIComponent(search)}&limit=50`:"?limit=50"
        const res=await fetch(`${API}${src.endpoint}${q}`,{headers:getAuthHeader()})
        const data=await res.json()
        if(data.success) setItems(data[src.listKey]||[])
      }catch{}
      setLoading(false)
    })()
  },[type,search])
  if(!src) return null
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <SectionHead>Pick from saved {src.label}</SectionHead>
        <button onClick={onManual} style={{fontSize:12,color:T.blue,background:"none",border:"none",cursor:"pointer",fontWeight:700,textDecoration:"underline"}}>Type manually →</button>
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${src.label}…`}
        style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:8,padding:"8px 11px",fontSize:13,outline:"none",marginBottom:8,boxSizing:"border-box"}}/>
      <div style={{maxHeight:200,overflowY:"auto",border:`1.5px solid ${T.border}`,borderRadius:10,background:T.surface}}>
        {loading ? <div style={{textAlign:"center",padding:32,color:"#aaa",fontSize:22}}>⏳</div>
        : items.length===0 ? (
          <div style={{textAlign:"center",padding:24,color:"#aaa",fontSize:13}}>
            No saved {src.label.toLowerCase()} found.<br/>
            <button onClick={onManual} style={{marginTop:8,color:T.blue,background:"none",border:"none",cursor:"pointer",fontWeight:700,textDecoration:"underline",fontSize:12}}>Enter title manually</button>
          </div>
        ) : items.map(item=>(
          <button key={item._id} onClick={()=>onSelect(item)}
            style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",borderBottom:`1px solid ${T.border}`,padding:"10px 14px",cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background="#EFF6FF"}
            onMouseLeave={e=>e.currentTarget.style.background="none"}>
            <div style={{fontWeight:700,fontSize:14,color:T.slate}}>{item.title}</div>
            <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",gap:8}}>
              {item.language&&<span>🌐 {item.language}</span>}
              {item.theme&&<span>🎨 {item.theme}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}


// ─── AlgorithmSuggestions ──────────────────────────────────────────────────
// Shows per-child flashcard suggestions based on BKT/SM-2/Elo outputs
// Only shown when scope=child and a child is selected
function AlgorithmSuggestions({ childId, getAuthHeader, onSelect }) {
  const [suggestions, setSuggestions] = useState([])
  const [loading,     setLoading]     = useState(false)
  const [expanded,    setExpanded]    = useState(true)

  useEffect(() => {
    if (!childId || childId === 'all') { setSuggestions([]); return }
    setLoading(true)
    fetch(`${API}/algorithm-results/${childId}/suggestions?limit=8`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => { if (d.success) setSuggestions(d.suggestions || []) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [childId])

  if (!childId || childId === 'all' || (!loading && suggestions.length === 0)) return null

  const PRIORITY_STYLE = {
    review:    { color:'#E65100', bg:'#FFF4EC', border:'#FFB347', icon:'⏰', label:'Review Due' },
    practice:  { color:'#7B2D8B', bg:'#F5F0FF', border:'#C4B5FD', icon:'🎯', label:'Needs Practice' },
    reinforce: { color:'#1D4ED8', bg:'#EFF6FF', border:'#93C5FD', icon:'💪', label:'Reinforce' },
    maintain:  { color:'#16A34A', bg:'#F0FDF4', border:'#86EFAC', icon:'✅', label:'Maintain' },
  }

  return (
    <div style={{ marginBottom:18, background:'#F8FAFD', borderRadius:12, border:'1.5px solid #E4ECF5', overflow:'hidden' }}>
      <button onClick={()=>setExpanded(e=>!e)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'10px 14px',
                 background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ fontSize:14 }}>🧠</span>
        <span style={{ fontSize:11, fontWeight:800, color:'#1A2E4A', flex:1 }}>Algorithm Suggestions for this child</span>
        {loading && <span style={{ fontSize:10, color:'#8FA5BC' }}>Loading…</span>}
        <span style={{ fontSize:11, color:'#8FA5BC' }}>{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && !loading && (
        <div style={{ padding:'0 12px 12px', display:'flex', flexDirection:'column', gap:7 }}>
          <div style={{ fontSize:11, color:'#8FA5BC', marginBottom:4, paddingLeft:2 }}>
            Based on BKT mastery · SM-2 schedule · Elo performance — click to add
          </div>
          {suggestions.map((s, i) => {
            const ps = PRIORITY_STYLE[s.priority] || PRIORITY_STYLE.maintain
            const mp = Math.round((s.avgMasteryProb || 0) * 100)
            return (
              <div key={i} onClick={() => onSelect({
                type: 'flashcard', title: s.setTitle || 'Untitled Set',
                duration: '20 min', notes: s.suggestionNote || '',
                domain: s.domains?.[0]
                  ? ({'language-literacy':'Language & Literacy','cognitive':'Cognitive Development',
                      'socio-emotional':'Socio-Emotional','physical-motor':'Physical & Motor',
                      'numeracy':'Early Numeracy'}[s.domains[0]] || 'Language & Literacy')
                  : 'Language & Literacy',
                // Pass set ID so the learning module's SetupScreen can pre-select the
                // flashcard set when the teacher taps ▶ from the Work Plan week view.
                flashcardSetId: s.flashcardSet?._id
                  ? String(s.flashcardSet._id)
                  : s.flashcardSet ? String(s.flashcardSet) : null,
              })}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px',
                         background:'white', borderRadius:9,
                         border:`1.5px solid ${ps.border}`, cursor:'pointer',
                         transition:'background .1s' }}
                onMouseEnter={e=>e.currentTarget.style.background=ps.bg}
                onMouseLeave={e=>e.currentTarget.style.background='white'}>
                <span style={{ fontSize:18, flexShrink:0 }}>🃏</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#1A2E4A', overflow:'hidden',
                                textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {s.setTitle || 'Untitled Set'}
                  </div>
                  <div style={{ fontSize:10, color:'#8FA5BC', marginTop:2 }}>{s.suggestionNote}</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3, flexShrink:0 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:ps.color, background:ps.bg,
                                 padding:'2px 7px', borderRadius:7, border:`1px solid ${ps.border}` }}>
                    {ps.icon} {ps.label}
                  </span>
                  <span style={{ fontSize:10, fontWeight:700, color:'#3C4F66' }}>BKT {mp}%</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── AddActivityModal ──────────────────────────────────────────────────────
function AddActivityModal({ onAdd, onClose, getAuthHeader, childId }) {
  const [form,setForm]=useState({type:"flashcard",title:"",duration:"20 min",notes:"",domain:"Language & Literacy"})
  const [pickerMode,setPickerMode]=useState(true)
  const [linkedId,setLinkedId]=useState(null)
  const at=getAType(form.type)
  const hasSource=!!MODULE_SOURCES[form.type]
  const canAdd=form.title.trim().length>0

  const handleTypeChange=(typeId)=>{
    setForm(f=>({...f,type:typeId,title:""})); setLinkedId(null); setPickerMode(!!MODULE_SOURCES[typeId])
  }
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:18,padding:28,width:540,maxWidth:"96vw",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,.25)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22}}>
          <div style={{fontFamily:"'Nunito',sans-serif",fontSize:20,fontWeight:900,color:T.slate}}>Add Activity</div>
          <button onClick={onClose} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:16,color:T.muted}}>×</button>
        </div>
        <div style={{marginBottom:18}}>
          <SectionHead>Activity Type</SectionHead>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6}}>
            {ACTIVITY_TYPES.map(t=>(
              <button key={t.id} onClick={()=>handleTypeChange(t.id)} style={{padding:"9px 4px",border:`1.5px solid ${form.type===t.id?t.border:T.border}`,borderRadius:10,background:form.type===t.id?t.bg:"#fff",cursor:"pointer",textAlign:"center"}}>
                <div style={{fontSize:18}}>{t.icon}</div>
                <div style={{fontSize:9,fontWeight:700,color:form.type===t.id?t.color:T.muted,marginTop:3,lineHeight:1.2}}>{t.label}</div>
              </button>
            ))}
          </div>
        </div>
        {/* Algorithm suggestions — shown when plan is for a specific child */}
        <AlgorithmSuggestions childId={childId} getAuthHeader={getAuthHeader}
  onSelect={item=>{
    setForm(f=>({...f, type:'flashcard', title:item.title, notes:item.notes, domain:item.domain}))
    setLinkedId(item.flashcardSetId || null); setPickerMode(false)
  }}/>

        {hasSource&&pickerMode ? (
          <div style={{marginBottom:18,background:T.surface,borderRadius:12,padding:16,border:`1.5px solid ${T.border}`}}>
            <ModuleItemPicker type={form.type} getAuthHeader={getAuthHeader}
              onSelect={item=>{setForm(f=>({...f,title:item.title}));setLinkedId(item._id);setPickerMode(false)}}
              onManual={()=>setPickerMode(false)}/>
          </div>
        ) : (
          <>
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                <SectionHead>Title</SectionHead>
                {hasSource&&<button onClick={()=>{setPickerMode(true);setLinkedId(null);setForm(f=>({...f,title:""}))}} style={{fontSize:12,color:T.blue,background:"none",border:"none",cursor:"pointer",fontWeight:700}}>← Browse {MODULE_SOURCES[form.type]?.label}</button>}
              </div>
              {linkedId&&<div style={{background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:8,padding:"6px 10px",fontSize:12,color:T.green,fontWeight:700,marginBottom:7}}>🔗 Linked: {form.title}</div>}
              <input value={form.title} onChange={e=>{setForm(f=>({...f,title:e.target.value}));setLinkedId(null)}}
                placeholder={`e.g. ${at.label} on Animals`}
                style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"10px 13px",fontSize:14,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}} autoFocus/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <SectionHead>Duration</SectionHead>
                <select value={form.duration} onChange={e=>setForm(f=>({...f,duration:e.target.value}))} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"10px 11px",fontSize:14,outline:"none"}}>
                  {DURATIONS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <SectionHead>NCF Domain</SectionHead>
                <select value={form.domain} onChange={e=>setForm(f=>({...f,domain:e.target.value}))} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"10px 11px",fontSize:14,outline:"none"}}>
                  {DOMAINS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginBottom:22}}>
              <SectionHead>Notes (optional)</SectionHead>
              <input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Instructions, materials, observations…"
                style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"10px 13px",fontSize:14,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={onClose} style={{flex:1,padding:12,background:"#fff",color:T.muted,border:`1.5px solid ${T.border}`,borderRadius:10,fontWeight:700,fontSize:14,cursor:"pointer"}}>Cancel</button>
              <button onClick={()=>{if(canAdd){onAdd({...form,id:`a-${Date.now()}`,completed:false,linkedId:linkedId||null});onClose()}}}
                disabled={!canAdd}
                style={{flex:2,padding:12,background:canAdd?at.color:"#D1D5DB",color:"#fff",border:"none",borderRadius:10,fontWeight:700,fontSize:14,cursor:canAdd?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                <span style={{fontSize:16}}>{at.icon}</span> Add Activity
              </button>
            </div>
          </>
        )}
        {hasSource&&pickerMode&&<button onClick={onClose} style={{width:"100%",marginTop:10,padding:"10px",background:"#fff",color:T.muted,border:`1.5px solid ${T.border}`,borderRadius:9,fontWeight:700,fontSize:13,cursor:"pointer"}}>Cancel</button>}
      </div>
    </div>
  )
}

// ─── ActivityCard ──────────────────────────────────────────────────────────
function ActivityCard({ act, actIdx, onUpdate, onDelete, onMove, total, isFuture, onStart }) {
  const [expanded,setExpanded]=useState(false)
  const at=getAType(act.type)
  return (
    <div style={{borderLeft:`3px solid ${act.completed?"#16A34A":at.border}`,background:act.completed?"#F0FDF4":at.bg,borderRadius:"0 10px 10px 0",padding:"8px 12px",marginBottom:7,cursor:"pointer"}} onClick={()=>setExpanded(e=>!e)}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <input type="checkbox" checked={act.completed} onClick={e=>e.stopPropagation()} onChange={e=>{if(isFuture)return;onUpdate({...act,completed:e.target.checked})}}
          style={{width:15,height:15,accentColor:"#16A34A",flexShrink:0,cursor:isFuture?"not-allowed":"pointer",opacity:isFuture?0.35:1}}/>
        <span style={{fontSize:15}}>{at.icon}</span>
        <span style={{fontSize:13,fontWeight:700,color:act.completed?"#166534":T.slate,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{act.title}</span>
        {act.completed
          ? <span style={{fontSize:10,fontWeight:700,color:"#166534",background:"#DCFCE7",borderRadius:6,padding:"1px 7px",flexShrink:0}}>✅ Done</span>
          : <span style={{fontSize:11,color:T.muted,background:"#fff",borderRadius:8,padding:"1px 7px",flexShrink:0,border:`1px solid ${T.border}`}}>{act.duration}</span>}
        {!act.completed&&!isFuture&&onStart&&TYPE_TO_LEARN_ROUTE[act.type]&&(
          <button onClick={e=>{e.stopPropagation();onStart()}}
            style={{fontSize:11,padding:"2px 8px",background:T.blue,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontWeight:700,flexShrink:0}}>▶</button>
        )}
        <span style={{fontSize:10,color:"#D1D5DB",flexShrink:0}}>{expanded?"▲":"▼"}</span>
      </div>
      {expanded&&(
        <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div><div style={{fontSize:10,fontWeight:700,color:T.muted,marginBottom:4}}>TYPE</div>
              <select value={act.type} onChange={e=>onUpdate({...act,type:e.target.value})} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:7,padding:"5px 7px",fontSize:12,outline:"none"}}>
                {ACTIVITY_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select></div>
            <div><div style={{fontSize:10,fontWeight:700,color:T.muted,marginBottom:4}}>DURATION</div>
              <select value={act.duration} onChange={e=>onUpdate({...act,duration:e.target.value})} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:7,padding:"5px 7px",fontSize:12,outline:"none"}}>
                {DURATIONS.map(d=><option key={d} value={d}>{d}</option>)}
              </select></div>
          </div>
          <div style={{marginBottom:6}}><div style={{fontSize:10,fontWeight:700,color:T.muted,marginBottom:4}}>TITLE</div>
            <input value={act.title} onChange={e=>onUpdate({...act,title:e.target.value})} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:7,padding:"6px 9px",fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
          <div style={{marginBottom:8}}><div style={{fontSize:10,fontWeight:700,color:T.muted,marginBottom:4}}>NOTES</div>
            <input value={act.notes||""} onChange={e=>onUpdate({...act,notes:e.target.value})} placeholder="Optional…" style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:7,padding:"6px 9px",fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
          <div style={{marginBottom:8}}><div style={{fontSize:10,fontWeight:700,color:T.muted,marginBottom:4}}>NCF DOMAIN</div>
            <select value={act.domain} onChange={e=>onUpdate({...act,domain:e.target.value})} style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:7,padding:"5px 7px",fontSize:12,outline:"none"}}>
              {DOMAINS.map(d=><option key={d} value={d}>{d}</option>)}
            </select></div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:4}}>
              {actIdx>0&&<button onClick={()=>onMove(-1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 9px",fontSize:12,cursor:"pointer",color:T.blue}}>↑</button>}
              {actIdx<total-1&&<button onClick={()=>onMove(1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 9px",fontSize:12,cursor:"pointer",color:T.blue}}>↓</button>}
            </div>
            <button onClick={onDelete} style={{background:"#FEF2F2",border:"none",borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:T.red,fontWeight:700}}>Remove</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── SavedPlansPanel ───────────────────────────────────────────────────────
function SavedPlansPanel({ getAuthHeader, onLoad, onClose }) {
  const [plans,setPlans]=useState([]), [loading,setLoading]=useState(true), [search,setSearch]=useState(""), [deleting,setDeleting]=useState(null)
  const load=async()=>{
    setLoading(true)
    try{
      const q=search?`&search=${encodeURIComponent(search)}`:""
      const res=await fetch(`${API}/workplan?limit=50${q}`,{headers:getAuthHeader()})
      const data=await res.json()
      if(data.success) setPlans(data.plans||data.workPlans||[])
    }catch{}
    setLoading(false)
  }
  useEffect(()=>{load()},[search])
  const del=async id=>{
    setDeleting(id)
    try{await fetch(`${API}/workplan/${id}`,{method:"DELETE",headers:getAuthHeader()}); setPlans(p=>p.filter(wp=>wp._id!==id))}catch{}
    setDeleting(null)
  }
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:20,width:900,maxWidth:"96vw",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 28px 64px rgba(0,0,0,.28)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"20px 26px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:14}}>
          <div style={{fontFamily:"'Nunito',sans-serif",fontSize:20,fontWeight:900,color:T.slate,flex:1}}>Saved Work Plans</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search plans…"
            style={{border:`1.5px solid ${T.border}`,borderRadius:9,padding:"8px 13px",fontSize:13,outline:"none",width:220}}/>
          <button onClick={onClose} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:16,color:T.muted}}>×</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:20}}>
          {loading ? <div style={{textAlign:"center",padding:60,fontSize:32}}>⏳</div>
          : plans.length===0 ? <div style={{textAlign:"center",padding:60,color:T.muted,fontSize:15}}><div style={{fontSize:40,marginBottom:12}}>📅</div>No saved plans yet</div>
          : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
              {plans.map(p=>(
                <div key={p._id} style={{background:"#fff",borderRadius:14,padding:18,border:`1.5px solid ${T.border}`}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                  <div style={{fontWeight:800,fontSize:15,color:T.slate,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.title}</div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:10}}>{p.weekLabel||formatWeek(p.weekStart)}</div>
                  <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                    <Chip label={p.child?.name||p.classroom?.name||"All Children"} color={T.blue} bg="#EFF6FF"/>
                    <Chip label={p.status||"active"} color={T.green} bg="#F0FDF4"/>
                  </div>
                  <div style={{fontSize:11,color:"#9CA3AF",marginBottom:14}}>{new Date(p.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>onLoad(p)} style={{flex:1,padding:"8px 0",background:T.nightInk,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>Load & Edit</button>
                    <button onClick={()=>del(p._id)} style={{padding:"8px 13px",background:"#FEF2F2",color:T.red,border:`1px solid #FECACA`,borderRadius:8,fontSize:13,cursor:"pointer"}}>{deleting===p._id?"…":"🗑"}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── ParentWorkPlanView ────────────────────────────────────────────────────
function ParentWorkPlanView({ getAuthHeader }) {
  const [selChild,setSelChild]=useState(null), [selPlan,setSelPlan]=useState(null)
  const [activeDay,setActiveDay]=useState(getTodayIdx()), [children,setChildren]=useState([])
  const [loading,setLoading]=useState(true), [error,setError]=useState("")

  useEffect(()=>{
    ;(async()=>{
      try{
        const res=await fetch(`${API}/children?limit=50`,{headers:getAuthHeader()})
        const data=await res.json()
        const list=data.children||[]
        setChildren(list); if(list.length) setSelChild(list[0]._id)
      }catch{setError("Could not load children.")}
      setLoading(false)
    })()
  },[])

  useEffect(()=>{
    if(!selChild) return
    setSelPlan(null); setError("")
    ;(async()=>{
      try{
        const res=await fetch(`${API}/workplan/child/${selChild}/current`,{headers:getAuthHeader()})
        const data=await res.json()
        if(data.success) setSelPlan(backendToPlan(data.plan))
        else setError("No work plan found for this week.")
      }catch{setError("Could not load work plan.")}
    })()
  },[selChild])

  if(loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column",gap:14}}><div style={{width:28,height:28,border:`3px solid ${T.border}`,borderTop:`3px solid ${T.agniCore}`,borderRadius:"50%",animation:"spin .7s linear infinite"}}/><div style={{fontSize:14,color:T.muted}}>Loading this week's plan…</div><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>

  return (
    <div style={{padding:"28px 32px 80px",maxWidth:920,margin:"0 auto",fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Nunito',sans-serif",fontSize:26,fontWeight:900,color:T.slate,marginBottom:4}}>This Week's Plan</div>
        <div style={{fontSize:14,color:T.muted}}>{selPlan?formatWeek(selPlan.weekOf):"Current week"}</div>
      </div>
      {children.length>1&&(
        <div style={{display:"flex",gap:8,marginBottom:22,flexWrap:"wrap"}}>
          {children.map(c=>(
            <button key={c._id} onClick={()=>setSelChild(c._id)} style={{padding:"7px 18px",borderRadius:50,fontWeight:700,fontSize:13,cursor:"pointer",border:`2px solid ${selChild===c._id?T.agniCore:T.border}`,background:selChild===c._id?T.agniCore:"#fff",color:selChild===c._id?"#fff":T.slate}}>{c.name}</button>
          ))}
        </div>
      )}
      {error&&<Card style={{marginBottom:20,background:"#FFF7ED",border:`1.5px solid #FCD34D`}}><div style={{fontSize:14,color:"#92400E",fontWeight:600}}>📋 {error}</div></Card>}
      {selPlan&&(
        <>
          {(()=>{
            const total=selPlan.days.reduce((s,d)=>s+d.activities.length,0)
            const done=selPlan.days.reduce((s,d)=>s+d.activities.filter(a=>a.completed).length,0)
            const pct=total>0?Math.round((done/total)*100):0
            return (
              <Card style={{marginBottom:20,display:"flex",alignItems:"center",gap:18}}>
                <div style={{width:52,height:52,borderRadius:"50%",background:pct===100?"#16A34A":T.agniCore,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:900,fontSize:18,flexShrink:0}}>{(selPlan.childLabel||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:800,fontSize:16,color:T.slate}}>{selPlan.childLabel}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:2}}>{selPlan.title} · {formatWeek(selPlan.weekOf)}</div>
                  <div style={{marginTop:8,height:6,background:T.border,borderRadius:8,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:pct===100?"#16A34A":T.agniCore,borderRadius:8,transition:"width .5s"}}/>
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:28,fontWeight:900,color:pct===100?"#16A34A":T.agniCore,fontFamily:"'Nunito',sans-serif"}}>{pct}%</div>
                  <div style={{fontSize:11,color:T.muted}}>{done}/{total} done</div>
                </div>
              </Card>
            )
          })()}
          <div style={{display:"flex",gap:7,marginBottom:20,overflowX:"auto",paddingBottom:4}}>
            {selPlan.days.map((d,di)=>{
              const count=d.activities.length, done=d.activities.filter(a=>a.completed).length, isToday=getTodayIdx()===di
              return (
                <button key={di} onClick={()=>setActiveDay(di)} style={{padding:"9px 18px",borderRadius:10,fontWeight:700,fontSize:13,cursor:"pointer",border:activeDay===di?`2px solid ${T.agniCore}`:`1.5px solid ${T.border}`,background:activeDay===di?T.agniCore:"#fff",color:activeDay===di?"#fff":T.slate,position:"relative",whiteSpace:"nowrap",flexShrink:0}}>
                  {isToday&&<span style={{position:"absolute",top:-5,left:"50%",transform:"translateX(-50%)",fontSize:8,background:T.agniCore,color:"#fff",padding:"0 5px",borderRadius:4,fontWeight:700,border:"2px solid #fff"}}>TODAY</span>}
                  {SHORT_DAYS[di]}
                  {count>0&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,color:done===count?"#16A34A":activeDay===di?"rgba(255,255,255,.7)":T.muted}}>{done}/{count}</span>}
                </button>
              )
            })}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {selPlan.days[activeDay]?.activities.length===0 ? (
              <Card style={{textAlign:"center",padding:40,color:T.muted}}><div style={{fontSize:36,marginBottom:10}}>📅</div><div style={{fontWeight:700,fontSize:15}}>No activities planned for this day</div></Card>
            ) : selPlan.days[activeDay].activities.map((act,ai)=>{
              const at=getAType(act.type)
              return (
                <div key={ai} style={{background:act.completed?"#F0FDF4":"#fff",borderRadius:14,padding:"16px 18px",border:`1.5px solid ${act.completed?"#86EFAC":T.border}`,borderLeft:`5px solid ${act.completed?"#16A34A":at.border}`}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
                    <span style={{fontSize:26,flexShrink:0}}>{at.icon}</span>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
                        <span style={{fontSize:15,fontWeight:800,color:act.completed?"#166534":T.slate}}>{act.title}</span>
                        {act.completed?<Chip label="✅ Completed" color="#166534" bg="#DCFCE7"/>:act._status==="skipped"?<Chip label="⏭ Skipped" color={T.muted} bg={T.surface}/>:<Chip label={at.label} color={at.color} bg={at.bg}/>}
                        <span style={{marginLeft:"auto",background:"#EFF6FF",color:T.blue,fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:8}}>⏱ {act.duration}</span>
                      </div>
                      {act.notes&&act.notes!==act.title&&<div style={{fontSize:13,color:T.muted,marginBottom:6}}>📌 {act.notes}</div>}
                      {act.domain&&<span style={{background:`${DOMAIN_COLORS[act.domain]||"#888"}18`,color:DOMAIN_COLORS[act.domain]||"#888",fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:8,border:`1px solid ${DOMAIN_COLORS[act.domain]||"#888"}40`}}>{act.domain}</span>}
                      {act._sessionNote&&<div style={{marginTop:10,background:"#EFF6FF",border:`1px solid #BFDBFE`,borderRadius:9,padding:"7px 11px",fontSize:12,color:T.blue}}>💬 Teacher's note: {act._sessionNote}</div>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT (Teacher / Admin)
// ═══════════════════════════════════════════════════════════════════════════
export default function WorkPlan() {
  const { getAuthHeader, user } = useAuth()
  const navigate = useNavigate()
  const role     = user?.role
  const isParent = role==="parent"
  const isTeacher= role==="teacher"||role==="admin"||role==="superadmin"

  if (isParent)  return <ParentWorkPlanView getAuthHeader={getAuthHeader} user={user}/>
  if (!isTeacher) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column",gap:12}}>
      <div style={{fontSize:36}}>🔒</div>
      <div style={{fontFamily:"'Nunito',sans-serif",fontSize:20,fontWeight:900,color:T.slate}}>Access Restricted</div>
      <div style={{fontSize:14,color:T.muted}}>Work Plan creation is available to teachers and admins only.</div>
    </div>
  )

  const [plan,        setPlan]        = useState(makeEmptyPlan())
  const [activeDay,   setActiveDay]   = useState(getTodayIdx())
  const [viewMode,    setViewMode]    = useState("week")
  const [addingToDay, setAddingToDay] = useState(null)
  const [showSaved,   setShowSaved]   = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [savedId,     setSavedId]     = useState(null)
  // saveStatus: null | "saved" | "unsaved" | "loaded-existing" | { type:"error", msg }
  const [saveStatus,  setSaveStatus]  = useState(null)
  const [children,    setChildren]    = useState([{_id:"all",name:"All Children"}])
  const [classrooms,  setClassrooms]  = useState([])
  const [loadingData, setLoadingData] = useState(true)

  // ── On mount: check for a target week passed via sessionStorage (e.g. from TeacherDashboard
  //    "Create Plan" button), then auto-load existing plan for that week if one exists
  useEffect(()=>{
    const targetWeek = sessionStorage.getItem('workplan_target_week')
    if (targetWeek) {
      sessionStorage.removeItem('workplan_target_week')
      // Parse as local time and set Monday of that week
      const mon = getMonday(targetWeek)
      const monStr = mon.toISOString().split('T')[0]
      setPlan(p => ({ ...p, weekOf: monStr, days: DAYS.map(day => ({ day, activities: [] })) }))
      setSavedId(null); setSaveStatus(null)
      // Try to load existing plan for that week (scope=all since from TeacherDashboard)
      ;(async()=>{ await autoLoadPlan(monStr, 'all', null, null) })()
      return // skip the normal current-week auto-load below
    }

    // Normal mount: auto-load current week's existing plan (scope=all default)
    ;(async()=>{ await autoLoadPlan(new Date(), 'all', null, null) })()
  },[])

  useEffect(()=>{
    ;(async()=>{
      try{
        const [cRes,clRes]=await Promise.all([
          fetch(`${API}/children?limit=200`,{headers:getAuthHeader()}),
          fetch(`${API}/classrooms?limit=100`,{headers:getAuthHeader()}).catch(()=>null),
        ])
        const cData=await cRes.json()
        if(cData.children?.length) setChildren([{_id:"all",name:"All Children"},...cData.children.map(u=>({_id:u._id,name:u.name,color:u.color||T.agniCore}))])
        if(clRes){const clData=await clRes.json(); if(clData.success) setClassrooms(clData.classrooms||clData.data||[])}
      }catch{}
      setLoadingData(false)
    })()
  },[])

  const updatePlan  = u => setPlan(p=>({...p,...u}))
  const markUnsaved = () => { if(savedId) setSaveStatus("unsaved") }

  const updateActivity=(di,ai,u)=>{
    const days=plan.days.map((d,i)=>i!==di?d:{...d,activities:d.activities.map((a,j)=>j!==ai?a:{...a,...u})})
    updatePlan({days}); markUnsaved()
  }
  const deleteActivity=(di,ai)=>{
    const days=plan.days.map((d,i)=>i!==di?d:{...d,activities:d.activities.filter((_,j)=>j!==ai)})
    updatePlan({days}); markUnsaved()
  }
  const moveActivity=(di,ai,dir)=>{
    const days=plan.days.map((d,i)=>{
      if(i!==di) return d
      const acts=[...d.activities], ni=ai+dir
      if(ni<0||ni>=acts.length) return d
      ;[acts[ai],acts[ni]]=[acts[ni],acts[ai]]
      return {...d,activities:acts}
    })
    updatePlan({days}); markUnsaved()
  }
  const addActivity=(di,act)=>{
    const days=plan.days.map((d,i)=>i!==di?d:{...d,activities:[...d.activities,act]})
    updatePlan({days}); markUnsaved()
  }

  const canSave=()=>{
    if(plan.scope==="child"&&!plan.childId) return {ok:false,msg:"Please select a child."}
    if(plan.scope==="classroom"&&!plan.classroomId) return {ok:false,msg:"Please select a classroom."}
    const totalActs=plan.days.reduce((s,d)=>s+d.activities.length,0)
    if(totalActs===0) return {ok:false,msg:"Add at least one activity before saving."}
    return {ok:true}
  }

  // ── loadExistingPlan: called when 409 → fetch + display the existing plan
  const loadExistingPlan = async (existingId) => {
    try{
      const res=await fetch(`${API}/workplan/${existingId}`,{headers:getAuthHeader()})
      const data=await res.json()
      if(data.success){
        const loaded=backendToPlan(data.plan)
        setPlan(loaded)
        setSavedId(existingId)
        setSaveStatus("loaded-existing")
        // clear the "loaded-existing" toast after 6s
        setTimeout(()=>setSaveStatus(prev=>prev==="loaded-existing"?null:prev),6000)
      }
    }catch{}
  }

  // ── autoLoadPlan: try to fetch an existing plan for the given week/scope/target
  // Called whenever week, scope, childId, or classroomId changes.
  // Silently loads into state if found; does nothing if not found.
  const autoLoadPlan = async (weekOf, scope, childId, classroomId) => {
    try {
      const mon    = getMonday(weekOf)
      const monStr = mon.toISOString().split('T')[0]
      // Build query: always filter by weekStart; add scope and target if specific
      let query = `${API}/workplan?weekStart=${monStr}&limit=1`
      if (scope === 'child'     && childId)     query += `&scope=child&childId=${childId}`
      else if (scope === 'classroom' && classroomId) query += `&scope=classroom&classroomId=${classroomId}`
      else                                           query += `&scope=all`

      const res  = await fetch(query, { headers: getAuthHeader() })
      const data = await res.json()
      const existing = (data.plans || data.workPlans || [])[0]
      if (existing) {
        const full = await fetch(`${API}/workplan/${existing._id}`, { headers: getAuthHeader() })
        const fd   = await full.json()
        if (fd.success) {
          setPlan(backendToPlan(fd.plan))
          setSavedId(fd.plan._id)
          setSaveStatus(null)
          return true  // found and loaded
        }
      }
    } catch {}
    return false  // nothing found
  }

  const doSave=async()=>{
    const v=canSave()
    if(!v.ok){setSaveStatus({type:"error",msg:v.msg});return}
    setSaving(true); setSaveStatus(null)
    try{
      const body=planToBackend(plan)
      const method=savedId?"PUT":"POST"
      const url=savedId?`${API}/workplan/${savedId}`:`${API}/workplan`
      const res=await fetch(url,{method,headers:{...getAuthHeader(),"Content-Type":"application/json"},body:JSON.stringify(body)})
      const data=await res.json()

      if(res.status===409 && data.existing?._id){
        // Plan already exists → silently load it
        await loadExistingPlan(data.existing._id)
      } else if(data.success){
        const saved=data.plan||data.workPlan
        setSavedId(saved._id)
        setSaveStatus("saved")
        if(plan.scope==="child"&&plan.childId){
          const kid=children.find(c=>String(c._id)===String(plan.childId))
          updatePlan({childLabel:kid?.name||"All Children"})
        }
        setTimeout(()=>setSaveStatus(prev=>prev==="saved"?null:prev),4000)
      } else {
        setSaveStatus({type:"error",msg:data.message||"Save failed."})
      }
    }catch{setSaveStatus({type:"error",msg:"Network error. Please try again."})}
    setSaving(false)
  }

  const handleLoadSaved=async(p)=>{
    setShowSaved(false)
    try{
      const res=await fetch(`${API}/workplan/${p._id}`,{headers:getAuthHeader()})
      const data=await res.json()
      if(data.success){setPlan(backendToPlan(data.plan));setSavedId(data.plan._id);setSaveStatus(null);setViewMode("week")}
    }catch{}
  }

  const handleNewPlan=()=>{setPlan(makeEmptyPlan());setSavedId(null);setSaveStatus(null)}
  const handlePrintWeek=async()=>{
    generateWeekPDF(plan)
    if(savedId) try{await fetch(`${API}/workplan/${savedId}/use`,{method:"POST",headers:getAuthHeader()})}catch{}
  }

  const totalActs     = plan.days.reduce((s,d)=>s+d.activities.length,0)
  const completedActs = plan.days.reduce((s,d)=>s+d.activities.filter(a=>a.completed).length,0)
  const progressPct   = totalActs>0?Math.round((completedActs/totalActs)*100):0

  const planMonday    = getMonday(new Date(plan.weekOf))
  const currentMonday = getMonday(new Date())
  const isCurrentWeek = planMonday.toDateString()===currentMonday.toDateString()
  const isPastWeek    = planMonday<currentMonday
  const weekCtx       = isCurrentWeek?"current":isPastWeek?"past":"future"

  const saveLabel = saving?"Saving…":saveStatus==="saved"?"✓ Saved":saveStatus==="unsaved"?"Save changes":savedId?"Saved":"Save plan"
  const saveBg    = saving?"#9CA3AF":saveStatus==="saved"?T.green:saveStatus==="unsaved"?T.agniCore:savedId?T.green:T.agniCore

  return (
    <div style={{fontFamily:"'Inter',system-ui,sans-serif",background:"#F3F4F6",minHeight:"100vh",color:T.slate}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap');
        @keyframes spin    { to { transform:rotate(360deg) } }
        @keyframes wp-pulse{ 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes pulse   { 0%,100%{box-shadow:0 0 0 0 rgba(234,88,12,.4)} 50%{box-shadow:0 0 0 6px rgba(234,88,12,0)} }
        * { box-sizing:border-box; }
      `}</style>

      {/* ══ TOP BAR ══ */}
      <div style={{background:T.nightInk,color:"#fff",padding:"0 28px",display:"flex",alignItems:"center",justifyContent:"space-between",height:60,borderBottom:`1px solid rgba(255,255,255,.06)`}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{fontFamily:"'Nunito',sans-serif",fontSize:18,fontWeight:900,color:"#fff"}}>Vidyagni</div>
          <div style={{width:1,height:20,background:"rgba(255,255,255,.2)"}}/>
          <div style={{fontWeight:600,fontSize:14,color:"rgba(255,255,255,.7)"}}>Work Plan</div>
          {savedId&&isCurrentWeek&&(
            <div style={{background:"rgba(22,163,74,.2)",color:"#4ADE80",fontSize:11,fontWeight:700,padding:"3px 12px",borderRadius:20,border:"1px solid rgba(22,163,74,.35)",display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:"#4ADE80",display:"inline-block",animation:"wp-pulse 2s infinite"}}/>
              Active this week
            </div>
          )}
          {savedId&&!isCurrentWeek&&(
            <div style={{background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.6)",fontSize:11,fontWeight:700,padding:"3px 12px",borderRadius:20}}>📌 Saved</div>
          )}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {saveStatus&&typeof saveStatus==="object"&&saveStatus.type==="error"&&(
            <span style={{fontSize:12,color:"#FCA5A5",fontWeight:600,maxWidth:260}}>⚠️ {saveStatus.msg}</span>
          )}
          <button onClick={()=>setShowSaved(true)} style={{background:"rgba(255,255,255,.1)",color:"#fff",border:"1px solid rgba(255,255,255,.2)",borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>📂 Saved Plans</button>
          <button onClick={handleNewPlan} style={{background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.75)",border:"1px solid rgba(255,255,255,.15)",borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>+ New Plan</button>
          <button onClick={doSave} disabled={saving}
            style={{background:saveBg,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontWeight:700,fontSize:14,cursor:saving?"default":"pointer",minWidth:110,animation:!savedId&&!saving?"pulse 2s infinite":"none"}}>
            {saveLabel}
          </button>
          <button onClick={handlePrintWeek} style={{background:"rgba(255,255,255,.12)",color:"#fff",border:"1px solid rgba(255,255,255,.2)",borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>🖨️ Print</button>
        </div>
      </div>

      {/* ══ PLAN CONFIG BAR ══ */}
      <div style={{background:"#fff",borderBottom:`1px solid ${T.border}`,padding:"14px 28px",display:"flex",alignItems:"flex-start",gap:20,flexWrap:"wrap"}}>
        <div style={{minWidth:200}}>
          <SectionHead>Plan Title</SectionHead>
          <input value={plan.title} onChange={e=>{updatePlan({title:e.target.value});markUnsaved()}}
            style={{fontWeight:700,fontSize:15,color:T.slate,border:`1.5px solid ${T.border}`,borderRadius:8,padding:"7px 11px",outline:"none",fontFamily:"inherit",width:"100%",background:T.surface}}
            onFocus={e=>e.target.style.borderColor=T.agniCore} onBlur={e=>e.target.style.borderColor=T.border}/>
        </div>
        <div>
          <SectionHead>Week of</SectionHead>
          <input type="date" value={plan.weekOf}
            onChange={async e=>{
              const picked=e.target.value
              const mon=getMonday(picked)
              const monStr=mon.toISOString().split("T")[0]
              const isFuture=mon>getMonday(new Date())
              // Snap to Monday, reset days if future week
              setPlan(p=>({...p,weekOf:monStr,days:isFuture?DAYS.map(day=>({day,activities:[]})):p.days}))
              setSavedId(null); setSaveStatus(null)
              // Load existing plan for this week+scope+target combination
              await autoLoadPlan(monStr, plan.scope, plan.childId, plan.classroomId)
            }}
            style={{border:`1.5px solid ${T.border}`,borderRadius:8,padding:"7px 10px",fontSize:14,outline:"none",color:T.slate,fontWeight:600}}
            onFocus={e=>e.target.style.borderColor=T.agniCore} onBlur={e=>e.target.style.borderColor=T.border}/>
        </div>
        <div>
          <SectionHead>Assign to</SectionHead>
          <div style={{display:"flex",gap:6}}>
            {[["all","All Children"],["child","One Child"],["classroom","Classroom"]].map(([v,l])=>(
              <button key={v} onClick={async ()=>{
                  updatePlan({scope:v,childId:null,childLabel:"All Children",classroomId:null,classroomLabel:""})
                  setSavedId(null); setSaveStatus(null)
                  // If switching to 'all', immediately try to load existing all-scope plan
                  if (v === 'all') await autoLoadPlan(plan.weekOf, 'all', null, null)
                  // For child/classroom: wait until target is selected before loading
                }}
                style={{padding:"7px 14px",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",border:`1.5px solid ${plan.scope===v?T.agniCore:T.border}`,background:plan.scope===v?"#FFF7ED":"#fff",color:plan.scope===v?T.agniCore:T.muted}}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {plan.scope==="child"&&(
          <div>
            <SectionHead>Select Child</SectionHead>
            <select value={plan.childId||""}
              onChange={async e=>{
                const kid=children.find(c=>String(c._id)===e.target.value)
                const newChildId = e.target.value||null
                updatePlan({childId:newChildId,childLabel:kid?.name||"All Children"})
                setSavedId(null); setSaveStatus(null)
                if (newChildId) await autoLoadPlan(plan.weekOf, 'child', newChildId, null)
              }}
              style={{border:`1.5px solid ${plan.childId?T.border:"#FCA5A5"}`,borderRadius:8,padding:"7px 11px",fontSize:14,outline:"none",fontWeight:600,color:plan.childId?T.slate:"#DC2626",minWidth:180}}>
              <option value="">— Select child —</option>
              {children.filter(c=>c._id!=="all").map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            {!plan.childId&&<div style={{fontSize:11,color:T.red,marginTop:4}}>Required to save</div>}
          </div>
        )}
        {plan.scope==="classroom"&&(
          <div>
            <SectionHead>Select Classroom</SectionHead>
            <select value={plan.classroomId||""}
              onChange={async e=>{
                const cl=classrooms.find(c=>String(c._id)===e.target.value)
                const newClassId = e.target.value||null
                updatePlan({classroomId:newClassId,classroomLabel:cl?.name||""})
                setSavedId(null); setSaveStatus(null)
                if (newClassId) await autoLoadPlan(plan.weekOf, 'classroom', null, newClassId)
              }}
              style={{border:`1.5px solid ${plan.classroomId?T.border:"#FCA5A5"}`,borderRadius:8,padding:"7px 11px",fontSize:14,outline:"none",fontWeight:600,color:plan.classroomId?T.slate:"#DC2626",minWidth:180}}>
              <option value="">— Select classroom —</option>
              {classrooms.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            {!plan.classroomId&&<div style={{fontSize:11,color:T.red,marginTop:4}}>Required to save</div>}
          </div>
        )}
        <div>
          <SectionHead>Theme</SectionHead>
          <ThemeInput value={plan.theme} onChange={v=>{updatePlan({theme:v});markUnsaved()}}/>
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"flex-end",gap:16}}>
          {totalActs>0&&(
            <div style={{textAlign:"right"}}>
              <SectionHead>Progress</SectionHead>
              <div style={{display:"flex",alignItems:"center",gap:9}}>
                <div style={{width:100,height:7,background:T.border,borderRadius:8,overflow:"hidden"}}>
                  <div style={{width:`${progressPct}%`,height:"100%",background:progressPct===100?T.green:T.agniCore,borderRadius:8,transition:"width .4s"}}/>
                </div>
                <span style={{fontSize:13,fontWeight:800,color:T.slate}}>{completedActs}/{totalActs}</span>
              </div>
            </div>
          )}
          <div>
            <SectionHead>View</SectionHead>
            <div style={{display:"flex",background:T.surface,borderRadius:9,overflow:"hidden",border:`1.5px solid ${T.border}`}}>
              {[["week","📅 Week"],["day","📋 Day"],["summary","📊 Summary"]].map(([v,l])=>(
                <button key={v} onClick={()=>setViewMode(v)} style={{padding:"7px 16px",border:"none",background:viewMode===v?T.nightInk:"transparent",color:viewMode===v?"#fff":T.muted,fontWeight:700,fontSize:13,cursor:"pointer"}}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ══ ACTIVE PLAN BANNER ══ — only shown when this week's plan is saved */}
      <ActivePlanBanner
        plan={plan} savedId={savedId} isCurrentWeek={isCurrentWeek}
        completedActs={completedActs} totalActs={totalActs} progressPct={progressPct}
        onNavigateDay={di=>{setViewMode("day");setActiveDay(di)}}
      />

      {/* Loaded-existing banner — shown after 409 auto-load */}
      <LoadedExistingBanner show={saveStatus==="loaded-existing"}/>

      {/* Week context banner (past / future) */}
      {weekCtx!=="current"&&(
        <div style={{margin:"12px 28px 0",borderRadius:12,padding:"11px 18px",display:"flex",alignItems:"center",gap:12,background:weekCtx==="future"?"#EFF6FF":"#FFFBEB",border:`1.5px solid ${weekCtx==="future"?"#BFDBFE":"#FDE68A"}`}}>
          <span style={{fontSize:20}}>{weekCtx==="future"?"🔮":"📜"}</span>
          <div style={{flex:1,fontSize:14,fontWeight:600,color:weekCtx==="future"?"#1E40AF":"#92400E"}}>
            {weekCtx==="future"?`Planning ahead — ${formatWeek(plan.weekOf)}`:`Viewing past week — ${formatWeek(plan.weekOf)}`}
          </div>
          <button onClick={()=>{const mon=getMonday(new Date());setPlan(p=>({...p,weekOf:mon.toISOString().split("T")[0]}));setSavedId(null);setSaveStatus(null)}}
            style={{padding:"6px 14px",background:"#fff",border:`1.5px solid ${T.border}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",color:T.slate}}>
            ← Today's Week
          </button>
        </div>
      )}

      {/* ══ WEEK VIEW ══ */}
      {viewMode==="week"&&(
        <div style={{padding:"18px 28px 80px"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:12}}>
            {plan.days.map((dayPlan,di)=>{
              const isToday=getTodayIdx()===di&&isCurrentWeek
              const dayMin=dayPlan.activities.reduce((s,a)=>s+(parseInt(a.duration)||0),0)
              const doneCnt=dayPlan.activities.filter(a=>a.completed).length
              const dayDate=new Date(plan.weekOf); dayDate.setDate(dayDate.getDate()+di)
              const isFuture=dayDate.setHours(0,0,0,0)>new Date().setHours(0,0,0,0)
              return (
                <div key={di} style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:isToday?"0 4px 24px rgba(234,88,12,.15)":"0 1px 4px rgba(0,0,0,.05)",border:isToday?`2px solid ${T.agniCore}`:`1.5px solid ${T.border}`}}>
                  <div style={{background:isToday?T.agniCore:T.surface,padding:"11px 13px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <div style={{fontWeight:800,fontSize:14,color:isToday?"#fff":T.slate}}>{DAYS[di]}</div>
                        {isToday&&<span style={{fontSize:9,background:"rgba(255,255,255,.25)",color:"#fff",padding:"1px 6px",borderRadius:4,fontWeight:700}}>TODAY</span>}
                      </div>
                      <div style={{fontSize:11,color:isToday?"rgba(255,255,255,.7)":T.muted,marginTop:2}}>
                        {dayPlan.activities.length} act{dayPlan.activities.length!==1?"s":""} · {dayMin} min
                        {doneCnt>0&&<span style={{marginLeft:4,color:isToday?"rgba(255,255,255,.8)":"#16A34A",fontWeight:700}}>· {doneCnt} done</span>}
                      </div>
                    </div>
                    <button onClick={()=>generateDayPDF(plan,di)} style={{background:isToday?"rgba(255,255,255,.18)":"#fff",color:isToday?"#fff":T.muted,border:`1px solid ${isToday?"rgba(255,255,255,.3)":T.border}`,borderRadius:7,padding:"4px 8px",fontSize:12,cursor:"pointer"}}>🖨️</button>
                  </div>
                  <div style={{padding:"8px 8px 4px",minHeight:90}}>
                    {dayPlan.activities.map((act,ai)=>(
                      <ActivityCard key={act.id} act={act} actIdx={ai}
                        onUpdate={u=>updateActivity(di,ai,u)} onDelete={()=>deleteActivity(di,ai)}
                        onMove={dir=>moveActivity(di,ai,dir)} total={dayPlan.activities.length} isFuture={isFuture}
                        onStart={TYPE_TO_LEARN_ROUTE[act.type]?()=>navigate(TYPE_TO_LEARN_ROUTE[act.type],{state:{workplanId:plan._backendId,entryId:act._entryId,linkedId:act.linkedId,childId:plan.scope==="child"?plan.childId:null}}):undefined}/>
                    ))}
                    {dayPlan.activities.length===0&&<div style={{textAlign:"center",color:"#D1D5DB",fontSize:12,padding:"16px 0",fontStyle:"italic"}}>Empty</div>}
                  </div>
                  <div style={{padding:"0 8px 10px"}}>
                    <button onClick={()=>setAddingToDay(di)}
                      style={{width:"100%",padding:"7px",border:`2px dashed ${isToday?T.agniCore+"66":T.border}`,borderRadius:9,background:"none",color:isToday?T.agniCore:T.muted,fontWeight:700,fontSize:13,cursor:"pointer"}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=T.agniCore;e.currentTarget.style.color=T.agniCore}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor=isToday?T.agniCore+"66":T.border;e.currentTarget.style.color=isToday?T.agniCore:T.muted}}>
                      + Add
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{marginTop:16,background:"#fff",borderRadius:14,padding:"14px 18px",border:`1.5px solid ${T.border}`}}>
            <SectionHead>Weekly Notes</SectionHead>
            <textarea value={plan.notes} onChange={e=>{updatePlan({notes:e.target.value});markUnsaved()}}
              placeholder="Goals, observations, or reminders for this week…" rows={2}
              style={{width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"9px 13px",fontSize:14,outline:"none",fontFamily:"inherit",resize:"none",boxSizing:"border-box",color:T.slate}}
              onFocus={e=>e.target.style.borderColor=T.agniCore} onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>
        </div>
      )}

      {/* ══ DAY VIEW ══ */}
      {viewMode==="day"&&(
        <div style={{padding:"18px 28px 80px"}}>
          <div style={{display:"flex",gap:7,marginBottom:20,overflowX:"auto",paddingBottom:4}}>
            {DAYS.map((day,di)=>{
              const count=plan.days[di].activities.length, done=plan.days[di].activities.filter(a=>a.completed).length
              const isToday=getTodayIdx()===di&&isCurrentWeek
              return (
                <button key={di} onClick={()=>setActiveDay(di)} style={{padding:"9px 18px",border:activeDay===di?`2px solid ${T.agniCore}`:`1.5px solid ${T.border}`,borderRadius:10,background:activeDay===di?T.agniCore:"#fff",color:activeDay===di?"#fff":T.slate,fontWeight:700,fontSize:14,cursor:"pointer",whiteSpace:"nowrap",position:"relative"}}>
                  {isToday&&<span style={{position:"absolute",top:-6,left:"50%",transform:"translateX(-50%)",fontSize:8,background:T.agniCore,color:"#fff",padding:"1px 5px",borderRadius:3,fontWeight:700,border:"2px solid #fff",whiteSpace:"nowrap"}}>TODAY</span>}
                  {day}
                  {count>0&&<span style={{position:"absolute",top:-6,right:-6,background:done===count?T.green:T.agniCore,color:"#fff",borderRadius:"50%",width:18,height:18,fontSize:10,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900}}>{count}</span>}
                </button>
              )
            })}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:20}}>
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:22,color:T.slate}}>
                  {plan.days[activeDay].day}
                  {getTodayIdx()===activeDay&&isCurrentWeek&&<span style={{marginLeft:10,fontSize:12,background:T.agniCore,color:"#fff",padding:"3px 10px",borderRadius:20,fontWeight:700}}>Today</span>}
                  <span style={{fontSize:14,fontWeight:500,color:T.muted,marginLeft:10}}>{plan.days[activeDay].activities.length} {plan.days[activeDay].activities.length===1?"activity":"activities"}</span>
                </div>
                <button onClick={()=>generateDayPDF(plan,activeDay)} style={{background:T.nightInk,color:"#fff",border:"none",borderRadius:9,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer"}}>🖨️ Print Day Plan</button>
              </div>
              {plan.days[activeDay].activities.length===0 ? (
                <Card style={{textAlign:"center",padding:48,color:T.muted}}>
                  <div style={{fontSize:40,marginBottom:12}}>📅</div>
                  <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>No activities planned</div>
                  <div style={{fontSize:13}}>Click the button below to add your first activity</div>
                </Card>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {plan.days[activeDay].activities.map((act,ai)=>{
                    const at=getAType(act.type)
                    const dayDate=new Date(plan.weekOf); dayDate.setDate(dayDate.getDate()+activeDay)
                    const isFuture=dayDate.setHours(0,0,0,0)>new Date().setHours(0,0,0,0)
                    return (
                      <div key={act.id} style={{background:act.completed?"#F0FDF4":"#fff",borderRadius:14,padding:"16px 18px",border:`1.5px solid ${act.completed?"#86EFAC":T.border}`,borderLeft:`5px solid ${act.completed?"#16A34A":at.border}`}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:13}}>
                          <input type="checkbox" checked={act.completed} onChange={e=>{if(!isFuture)updateActivity(activeDay,ai,{completed:e.target.checked})}}
                            style={{width:18,height:18,accentColor:"#16A34A",marginTop:2,flexShrink:0,cursor:isFuture?"not-allowed":"pointer",opacity:isFuture?0.35:1}}/>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:20}}>{at.icon}</span>
                              <span style={{fontSize:15,fontWeight:800,color:act.completed?"#166534":T.slate}}>{act.title}</span>
                              {act.completed?<Chip label="✅ Done" color="#166534" bg="#DCFCE7"/>:<Chip label={at.label} color={at.color} bg={at.bg}/>}
                              <span style={{background:"#EFF6FF",color:T.blue,fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:8,marginLeft:"auto"}}>⏱ {act.duration}</span>
                            </div>
                            {act.notes&&<div style={{fontSize:13,color:T.muted,marginBottom:6}}>📌 {act.notes}</div>}
                            {act.domain&&<span style={{background:`${DOMAIN_COLORS[act.domain]||"#888"}18`,color:DOMAIN_COLORS[act.domain]||"#888",fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:8}}>{act.domain}</span>}
                          </div>
                          <div style={{display:"flex",gap:4,flexShrink:0}}>
                            {ai>0&&<button onClick={()=>moveActivity(activeDay,ai,-1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 9px",cursor:"pointer",color:T.blue}}>↑</button>}
                            {ai<plan.days[activeDay].activities.length-1&&<button onClick={()=>moveActivity(activeDay,ai,1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 9px",cursor:"pointer",color:T.blue}}>↓</button>}
                            <button onClick={()=>deleteActivity(activeDay,ai)} style={{background:"#FEF2F2",border:"none",borderRadius:6,padding:"5px 9px",cursor:"pointer",color:T.red}}>🗑</button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              <button onClick={()=>setAddingToDay(activeDay)}
                style={{width:"100%",marginTop:12,padding:13,border:`2px dashed ${T.border}`,borderRadius:12,background:"none",color:T.blue,fontWeight:700,fontSize:14,cursor:"pointer"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.agniCore;e.currentTarget.style.color=T.agniCore}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.blue}}>
                ➕ Add Activity
              </button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <Card>
                <SectionHead>Day Summary</SectionHead>
                {[["Activities",plan.days[activeDay].activities.length],["Completed",plan.days[activeDay].activities.filter(a=>a.completed).length],["Total Time",plan.days[activeDay].activities.reduce((s,a)=>s+(parseInt(a.duration)||0),0)+" min"]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid #F9FAFB`,fontSize:14}}>
                    <span style={{color:T.muted}}>{k}</span><span style={{fontWeight:800,color:T.slate}}>{v}</span>
                  </div>
                ))}
              </Card>
              <Card>
                <SectionHead>NCF Domains</SectionHead>
                {DOMAINS.map(d=>{
                  const cnt=plan.days[activeDay].activities.filter(a=>a.domain===d).length
                  if(cnt===0) return null
                  return (
                    <div key={d} style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
                      <div style={{width:9,height:9,borderRadius:"50%",background:DOMAIN_COLORS[d],flexShrink:0}}/>
                      <span style={{fontSize:12,color:T.muted,flex:1}}>{d}</span>
                      <span style={{fontSize:12,fontWeight:700,color:DOMAIN_COLORS[d]}}>{cnt}</span>
                    </div>
                  )
                })}
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ══ SUMMARY VIEW ══ */}
      {viewMode==="summary"&&(
        <div style={{padding:"24px 28px 80px",maxWidth:860,margin:"0 auto"}}>
          <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:24,color:T.slate,marginBottom:4}}>Weekly Summary</div>
          <div style={{fontSize:14,color:T.muted,marginBottom:24}}>{formatWeek(plan.weekOf)} · {plan.scope==="classroom"?plan.classroomLabel:plan.childLabel}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
            {[{label:"Total Activities",val:totalActs,icon:"📋",color:T.blue},{label:"Completed",val:completedActs,icon:"✅",color:T.green},{label:"Progress",val:`${progressPct}%`,icon:"📈",color:T.violet},{label:"Total Time",val:`${plan.days.reduce((s,d)=>s+d.activities.reduce((ss,a)=>ss+(parseInt(a.duration)||0),0),0)} min`,icon:"⏱",color:T.agniCore}].map(s=>(
              <Card key={s.label} style={{textAlign:"center",padding:"20px 14px"}}>
                <div style={{fontSize:28,marginBottom:6}}>{s.icon}</div>
                <div style={{fontFamily:"'Nunito',sans-serif",fontSize:28,fontWeight:900,color:s.color}}>{s.val}</div>
                <div style={{fontSize:12,color:T.muted,marginTop:3}}>{s.label}</div>
              </Card>
            ))}
          </div>
          <Card style={{marginBottom:16}}>
            <SectionHead>Activities per Day</SectionHead>
            <div style={{display:"flex",gap:8}}>
              {plan.days.map((d,di)=>(
                <div key={di} style={{flex:1,textAlign:"center",background:d.activities.length>0?"#FFF7ED":"#F9FAFB",borderRadius:10,padding:"12px 6px",border:`1.5px solid ${d.activities.length>0?"#FED7AA":T.border}`}}>
                  <div style={{fontSize:10,fontWeight:800,color:d.activities.length>0?T.agniCore:T.muted,textTransform:"uppercase",marginBottom:4}}>{SHORT_DAYS[di]}</div>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontSize:22,fontWeight:900,color:d.activities.length>0?T.agniCore:"#D1D5DB"}}>{d.activities.length}</div>
                  {d.activities.length>0&&<div style={{fontSize:10,color:T.muted,marginTop:2}}>{d.activities.filter(a=>a.completed).length} done</div>}
                </div>
              ))}
            </div>
          </Card>
          <Card style={{marginBottom:20}}>
            <SectionHead>NCF Domain Coverage</SectionHead>
            {Object.entries(DOMAIN_COLORS).map(([domain,color])=>{
              const cnt=plan.days.reduce((s,d)=>s+d.activities.filter(a=>a.domain===domain).length,0)
              const pct=totalActs>0?Math.round((cnt/totalActs)*100):0
              return (
                <div key={domain} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:600,color:T.slate}}>{domain}</span>
                    <span style={{fontSize:13,color:T.muted}}>{cnt} · {pct}%</span>
                  </div>
                  <div style={{height:8,background:T.border,borderRadius:8,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:8,transition:"width .5s"}}/>
                  </div>
                </div>
              )
            })}
          </Card>
          <button onClick={handlePrintWeek} style={{width:"100%",padding:18,background:T.nightInk,color:"#fff",border:"none",borderRadius:14,fontSize:15,fontWeight:800,cursor:"pointer"}}>🖨️ Print Full Week Plan (A4 Landscape)</button>
        </div>
      )}

      {/* ── Modals ── */}
      {addingToDay!==null&&<AddActivityModal onAdd={act=>addActivity(addingToDay,act)} onClose={()=>setAddingToDay(null)} getAuthHeader={getAuthHeader} childId={plan.scope==="child"?plan.childId:null}/>}
      {showSaved&&<SavedPlansPanel getAuthHeader={getAuthHeader} onLoad={handleLoadSaved} onClose={()=>setShowSaved(false)}/>}

      {/* ── Bottom status bar ── */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:`1px solid ${T.border}`,padding:"9px 28px",display:"flex",alignItems:"center",gap:14,fontSize:13,color:T.muted,zIndex:50}}>
        <span style={{fontWeight:700,color:T.slate}}>{plan.title}</span>
        <span style={{color:T.border}}>·</span>
        <span>{formatWeek(plan.weekOf)}</span>
        <span style={{color:T.border}}>·</span>
        <span>{plan.scope==="classroom"?plan.classroomLabel:plan.childLabel}</span>
        {totalActs>0&&<><span style={{color:T.border}}>·</span><span>{completedActs}/{totalActs} complete</span></>}
        {savedId&&isCurrentWeek&&(
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"#16A34A",display:"inline-block",animation:"wp-pulse 2s infinite"}}/>
            <span style={{fontSize:12,fontWeight:700,color:T.green}}>Active this week</span>
          </div>
        )}
        {saveStatus==="unsaved"&&<Chip label="Unsaved changes" color={T.agniCore} bg="#FFF7ED"/>}
        <div style={{flex:1}}/>
        <button onClick={()=>setAddingToDay(viewMode==="day"?activeDay:0)}
          style={{background:T.agniCore,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
          ➕ Add Activity
        </button>
      </div>
      <div style={{height:54}}/>
    </div>
  )
}